import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { POLE_LABELS, apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AttachmentPicker from "@/components/AttachmentPicker";
import { toast } from "sonner";

export default function Envoyer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPole = searchParams.get("from");
  const [poles, setPoles] = useState([]);
  const [sender, setSender] = useState("");
  const [receiver, setReceiver] = useState("");
  const [object, setObject] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiClient.get("/poles").then(({ data }) => {
      const active = data.filter((p) => p.is_active !== false);
      setPoles(active);
      if (fromPole && active.some((p) => p.id === fromPole)) setSender(fromPole);
    });
  }, [fromPole]);

  const poleOptions = useMemo(() => poles.map((p) => ({ id: p.id, name: p.name || POLE_LABELS[p.id] })), [poles]);

  const submit = async (e) => {
    e.preventDefault();
    if (!sender || !receiver || !object) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    if (sender === receiver) {
      toast.error("Le pôle expéditeur et destinataire doivent être différents");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("sender_pole", sender);
      fd.append("receiver_pole", receiver);
      fd.append("object", object);
      fd.append("description", description);
      files.forEach((f) => fd.append("files", f));
      const { data } = await apiClient.post("/requests", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Demande envoyée avec succès");
      navigate(`/pole/${data.receiver_pole}`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Topbar title="Envoyer une demande" subtitle="Transmettre des documents (PDF / photos) d'un pôle à un autre" />

      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-3xl w-full" data-testid="envoyer-page">
        <form onSubmit={submit} className="bg-white border border-zinc-200">
          <div className="px-5 sm:px-8 py-5 sm:py-7 border-b border-zinc-200">
            <div className="label-tiny text-zinc-400 mb-1">Étape 1</div>
            <h2 className="font-heading text-lg font-semibold tracking-tight">Routage</h2>
          </div>

          <div className="px-5 sm:px-8 py-5 sm:py-7 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 border-b border-zinc-200">
            <div className="space-y-2">
              <Label className="label-tiny text-zinc-500">Pôle expéditeur *</Label>
              <Select value={sender} onValueChange={setSender}>
                <SelectTrigger className="h-10 rounded-sm border-zinc-300" data-testid="sender-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {poleOptions.map(({ id, name }) => (
                    <SelectItem key={id} value={id}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="label-tiny text-zinc-500">Pôle destinataire *</Label>
              <Select value={receiver} onValueChange={setReceiver}>
                <SelectTrigger className="h-10 rounded-sm border-zinc-300" data-testid="receiver-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {poleOptions
                    .filter(({ id }) => id !== sender)
                    .map(({ id, name }) => (
                      <SelectItem key={id} value={id}>{name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="px-5 sm:px-8 py-5 sm:py-7 border-b border-zinc-200">
            <div className="label-tiny text-zinc-400 mb-1">Étape 2</div>
            <h2 className="font-heading text-lg font-semibold tracking-tight mb-6">Détails de la demande</h2>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="object" className="label-tiny text-zinc-500">Numéro de transaction *</Label>
                <Input
                  id="object"
                  value={object}
                  onChange={(e) => setObject(e.target.value)}
                  placeholder="Ex: TRX-2026-0001"
                  className="h-10 rounded-sm border-zinc-300 font-mono"
                  data-testid="object-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="label-tiny text-zinc-500">Description (optionnelle)</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Contexte ou instructions complémentaires..."
                  className="rounded-sm border-zinc-300 min-h-[100px]"
                  data-testid="description-input"
                />
              </div>
            </div>
          </div>

          <div className="px-5 sm:px-8 py-5 sm:py-7 border-b border-zinc-200">
            <div className="label-tiny text-zinc-400 mb-1">Étape 3 · optionnelle</div>
            <h2 className="font-heading text-lg font-semibold tracking-tight mb-2">Pièces jointes</h2>
            <p className="text-xs text-zinc-500 mb-5">Joignez un ou plusieurs PDF, images, ou prenez une photo de vos documents.</p>
            <AttachmentPicker files={files} setFiles={setFiles} testIdPrefix="envoyer" />
          </div>

          <div className="px-5 sm:px-8 py-4 sm:py-5 flex items-center justify-end gap-3 bg-zinc-50">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="rounded-sm h-10 px-5 border-zinc-300"
              data-testid="cancel-btn"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-10 px-6"
              data-testid="submit-btn"
            >
              {submitting ? "Envoi en cours..." : "Envoyer la demande"}
            </Button>
          </div>
        </form>
      </main>
    </>
  );
}
